import React, { useEffect, useCallback, useState, useRef } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  PageHeader,
  Button,
  Input,
  Modal,
  Radio,
  Table,
  Tag,
} from "antd";
import {
  AppstoreOutlined,
  UnorderedListOutlined,
  EditOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { handleApiError, handleApiSuccess, MyBreadcrumb } from "utils";
import {
  ticketProjectBoardService,
  ticketCreateService,
  ticketMoveService,
  ticketListCreateService,
  ticketListDeleteService,
} from "services/tickets";
import { TicketAccessGate, useTicketAccess } from "../_useTicketAccess";
import KanbanColumn from "../_components/KanbanColumn";
import TicketDrawer from "../_components/TicketDrawer";
import TicketBusy, { TicketProgressBar } from "../_components/TicketAsync";
import { PRIORITY_META, STATUS_META, userDisplayName, isOverdue, normalizeId } from "../constants";
import "../_styles.css";

function BoardContent() {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { canManage } = useTicketAccess();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [project, setProject] = useState(null);
  const [lists, setLists] = useState([]);
  const [ticketsByList, setTicketsByList] = useState({});
  const [view, setView] = useState("board");
  const [drawerTicketId, setDrawerTicketId] = useState(null);
  const [newListOpen, setNewListOpen] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [creatingForListId, setCreatingForListId] = useState(null);
  const [addingList, setAddingList] = useState(false);
  const [movingTicketId, setMovingTicketId] = useState(null);
  const [deletingListId, setDeletingListId] = useState(null);
  const hasLoadedRef = useRef(false);

  const loadBoard = useCallback(() => {
    if (!hasLoadedRef.current) setLoading(true);
    else setRefreshing(true);

    ticketProjectBoardService(id)
      .then((res) => {
        const item = res.data.item || {};
        setProject(item.project);
        setLists(item.lists || []);
        setTicketsByList(item.tickets_by_list || {});
        hasLoadedRef.current = true;

        const openTicket = new URLSearchParams(location.search).get("ticket");
        if (openTicket) setDrawerTicketId(openTicket);
      })
      .catch(handleApiError)
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  }, [id, location.search]);

  useEffect(loadBoard, [loadBoard]);

  const openTicketDrawer = (ticketId) => {
    const normalized = normalizeId(ticketId);
    setDrawerTicketId(normalized);
    const params = new URLSearchParams(location.search);
    params.set("ticket", normalized);
    history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
  };

  const closeTicketDrawer = () => {
    setDrawerTicketId(null);
    const params = new URLSearchParams(location.search);
    params.delete("ticket");
    const search = params.toString();
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : "" });
    loadBoard();
  };

  const onAddTicket = (listId, title) => {
    const payload = {
      project: normalizeId(id),
      list: normalizeId(listId),
      title: (title || "").trim(),
    };
    if (!payload.project || !payload.list || !payload.title) {
      handleApiError({ response: { data: { message: "Could not create ticket — missing project, list, or title." } } });
      return;
    }
    setCreatingForListId(normalizeId(listId));
    ticketCreateService(payload)
      .then((res) => {
        handleApiSuccess(res);
        loadBoard();
      })
      .catch(handleApiError)
      .finally(() => setCreatingForListId(null));
  };

  const onDropTicket = (ticketId, listId) => {
    setMovingTicketId(normalizeId(ticketId));
    ticketMoveService(normalizeId(ticketId), { list: normalizeId(listId) })
      .then(() => loadBoard())
      .catch(handleApiError)
      .finally(() => setMovingTicketId(null));
  };

  const onAddList = () => {
    if (!newListName.trim() || addingList) return;
    setAddingList(true);
    ticketListCreateService({ project: id, name: newListName.trim() })
      .then(() => {
        setNewListName("");
        setNewListOpen(false);
        loadBoard();
      })
      .catch(handleApiError)
      .finally(() => setAddingList(false));
  };

  const onDeleteList = (listId) => {
    setDeletingListId(normalizeId(listId));
    ticketListDeleteService(listId)
      .then(loadBoard)
      .catch(handleApiError)
      .finally(() => setDeletingListId(null));
  };

  const boardBusy =
    refreshing || !!creatingForListId || !!movingTicketId || addingList || !!deletingListId;

  const allTickets = lists.flatMap((l) => ticketsByList[normalizeId(l._id)] || []);

  const listColumns = [
    {
      title: "#",
      dataIndex: "ticket_number",
      key: "ticket_number",
      width: 100,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "List",
      key: "list",
      render: (_, r) => r.list?.name || "-",
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (p) => {
        const pm = PRIORITY_META[p] || PRIORITY_META.MEDIUM;
        return (
          <span className="priority-pill" style={{ background: pm.bg, color: pm.color }}>
            {pm.label}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => <Tag color={STATUS_META[s]?.color}>{STATUS_META[s]?.label}</Tag>,
    },
    {
      title: "Assignees",
      key: "assignees",
      render: (_, r) => (r.assignees || []).map((a) => userDisplayName(a)).join(", ") || "-",
    },
    {
      title: "Due",
      dataIndex: "due_date",
      key: "due_date",
      render: (d, r) =>
        d ? (
          <span className={isOverdue(d, r.status) ? "due-overdue" : ""}>{d}</span>
        ) : (
          "-"
        ),
    },
  ];

  if (loading && !project) {
    return <TicketBusy spinning tip="Loading board…" />;
  }

  return (
    <div className="ticket-system">
      <MyBreadcrumb
        items={[
          { label: "Home", path: "/" },
          { label: "Tickets", path: "/tickets/dashboard" },
          { label: "Projects", path: "/tickets/projects" },
          { label: project?.name || "Board", path: "" },
        ]}
      />

      <div className="board-toolbar">
        <div>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => history.push("/tickets/projects")}
          />
          <span style={{ fontWeight: 700, fontSize: 18, marginLeft: 4 }}>{project?.name}</span>
          {project?.description && (
            <span style={{ color: "#64748b", marginLeft: 12 }}>{project.description}</span>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Radio.Group value={view} onChange={(e) => setView(e.target.value)} buttonStyle="solid">
            <Radio.Button value="board">
              <AppstoreOutlined /> Board
            </Radio.Button>
            <Radio.Button value="list">
              <UnorderedListOutlined /> List
            </Radio.Button>
          </Radio.Group>
          {canManage && (
            <>
              <Button icon={<EditOutlined />} onClick={() => history.push("/tickets/projects/update/" + id)}>
                Settings
              </Button>
              <Button icon={<PlusOutlined />} onClick={() => setNewListOpen(true)}>
                Add List
              </Button>
            </>
          )}
        </div>
      </div>

      <TicketProgressBar active={boardBusy} />

      {view === "board" ? (
        <TicketBusy spinning={refreshing} tip="Refreshing board…">
          <div className="kanban-board">
            {lists.map((list) => (
              <KanbanColumn
                key={list._id}
                list={list}
                tickets={ticketsByList[normalizeId(list._id)] || []}
                canManage={canManage}
                creatingTicket={creatingForListId === normalizeId(list._id)}
                deletingList={deletingListId === normalizeId(list._id)}
                onAddTicket={onAddTicket}
                onTicketClick={(t) => openTicketDrawer(t._id)}
                onDropTicket={onDropTicket}
                onDeleteList={onDeleteList}
              />
            ))}
          </div>
        </TicketBusy>
      ) : (
        <Table
          className="list-view-table"
          rowKey="_id"
          dataSource={allTickets}
          columns={listColumns}
          loading={refreshing}
          pagination={{ pageSize: 20 }}
          onRow={(record) => ({
            onClick: () => openTicketDrawer(record._id),
          })}
        />
      )}

      <TicketDrawer
        ticketId={drawerTicketId}
        open={!!drawerTicketId}
        canManage={canManage}
        onClose={closeTicketDrawer}
        onUpdated={loadBoard}
      />

      <Modal
        title="New List"
        visible={newListOpen}
        confirmLoading={addingList}
        okText={addingList ? "Creating…" : "Create List"}
        onOk={onAddList}
        onCancel={() => !addingList && setNewListOpen(false)}
      >
        <Input
          placeholder="List name (e.g. Backlog, QA)"
          value={newListName}
          disabled={addingList}
          onChange={(e) => setNewListName(e.target.value)}
          onPressEnter={onAddList}
        />
      </Modal>
    </div>
  );
}

export default function TicketProjectBoard() {
  return (
    <TicketAccessGate>
      <BoardContent />
    </TicketAccessGate>
  );
}
